// Nhấp đúp ranh giới 2 cột ở tiêu đề DataGrid → tự căn độ rộng (như Excel). DevExtreme 22.2 không có sẵn.
//
// Cơ chế DevExtreme (columns_resizing_reordering): rê chuột gần ranh giới → hiện vạch ngăn
// `.dx-datagrid-columns-separator` (con trỏ col-resize); nhấn = bắt đầu kéo (hiện lớp phủ `.dx-datagrid-tracker`);
// thả = kết thúc, ẩn vạch ngăn + lớp phủ. Sắp xếp theo cột chạy trên click (dxclick) của ô tiêu đề.
//
// KHÔNG dựa vào sự kiện dblclick: đích của nó đổi theo thao tác thật — chuột nhích 1-2px giữa 2 lần nhấp làm
// DevExtreme hiện lại vạch ngăn và bắt đầu kéo lần nữa, dblclick khi đó rơi vào lớp phủ/khung lưới chứ không phải
// tiêu đề. Thay vào đó tự nhận "2 lần nhấn liên tiếp tại cùng ranh giới" ngay ở pointerdown:
// - Nhấn tại ranh giới = trúng vạch ngăn HOẶC trong tiêu đề, cách mép phải 1 ô ≤ 6px.
// - Lần nhấn 1: để DevExtreme xử lý (bắt đầu kéo) — nhấn rồi thả không kéo thì không đổi gì.
// - Lần nhấn 2 (≤ 500ms, lệch ≤ 8px): tự căn; chặn trọn lần nhấn này (pointerdown/mousedown/pointerup/mouseup/
//   click/dblclick) để DevExtreme không bắt đầu kéo và không sắp xếp.
// - Click sát ranh giới trong tiêu đề luôn bị chặn: nhắm vạch ngăn, không phải bấm sắp xếp.
// - Giữa 2 lần nhấn: giữ con trỏ kéo (class ant-grid-boundary-press) — DevExtreme trả con trỏ "ngón tay" của tiêu đề
//   ngay khi thả lần 1, trông như sắp bấm sắp xếp.
//
// attachHeaderBoundaryDblClick(element, { onAutoFit, hint }) → hàm gỡ.

const EDGE_TOLERANCE = 6;
const SECOND_PRESS_MS = 500;
const SECOND_PRESS_DISTANCE = 8;
// Sau lần nhấn 2: các sự kiện còn lại của cú nhấn đó (thả, click, dblclick) bị chặn trong khoảng này.
const SWALLOW_MS = 700;
// Nút trong tiêu đề (lọc theo tiêu đề, chọn tất cả…) giữ nguyên hành vi.
const INTERACTIVE = '.dx-header-filter, .dx-checkbox, .dx-command-select, input, button, a';

export const attachHeaderBoundaryDblClick = (element, { onAutoFit, hint } = {}) => {
    if (!element || typeof onAutoFit !== 'function') return () => {};
    const closest = (e, selector) => (e.target && e.target.closest ? e.target.closest(selector) : null);
    const onSeparator = (e) => Boolean(closest(e, '.dx-datagrid-columns-separator'));
    const nearHeaderBoundary = (e) => {
        const row = closest(e, '.dx-header-row');
        if (!row || !element.contains(row) || closest(e, INTERACTIVE)) return false;
        return Array.from(row.children).some((td) => td.offsetWidth > 0 && Math.abs(e.clientX - td.getBoundingClientRect().right) <= EDGE_TOLERANCE);
    };
    const block = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };
    const PRESS_CLASS = 'ant-grid-boundary-press';
    let pressTimer = null;
    const endPressCursor = () => {
        clearTimeout(pressTimer);
        pressTimer = null;
        element.classList.remove(PRESS_CLASS);
    };
    const startPressCursor = () => {
        element.classList.add(PRESS_CLASS);
        clearTimeout(pressTimer);
        pressTimer = setTimeout(endPressCursor, SECOND_PRESS_MS);
    };

    let lastPress = { at: 0, x: 0 };
    let swallowUntil = 0;
    const onPointerDown = (e) => {
        if (typeof e.button === 'number' && e.button !== 0) return;
        const now = Date.now();
        if (!onSeparator(e) && !nearHeaderBoundary(e)) {
            lastPress = { at: 0, x: 0 };
            endPressCursor();
            return;
        }
        const second = now - lastPress.at <= SECOND_PRESS_MS && Math.abs(e.clientX - lastPress.x) <= SECOND_PRESS_DISTANCE;
        if (!second) {
            lastPress = { at: now, x: e.clientX };
            startPressCursor();
            return;
        }
        lastPress = { at: 0, x: 0 };
        swallowUntil = now + SWALLOW_MS;
        endPressCursor();
        // Chặn cả các xử lý khác gắn trên cùng phần tử (vd tự kéo đổi độ rộng của AntGrid).
        e.stopImmediatePropagation();
        block(e);
        onAutoFit(e);
    };
    // Chuột rời xa ranh giới vừa nhấn → trả con trỏ bình thường.
    const onPointerMove = (e) => {
        if (element.classList.contains(PRESS_CLASS) && Math.abs(e.clientX - lastPress.x) > SECOND_PRESS_DISTANCE) endPressCursor();
    };
    // Phần còn lại của lần nhấn 2 (kể cả mousedown tương thích) không tới DevExtreme.
    const onFollowUp = (e) => {
        if (Date.now() <= swallowUntil) block(e);
    };
    // Click sát ranh giới: không phải thao tác sắp xếp (nhắm vạch ngăn) → luôn chặn.
    const onClick = (e) => {
        if (Date.now() <= swallowUntil || nearHeaderBoundary(e)) block(e);
    };
    const onOver = (e) => {
        if (!hint) return;
        const separator = closest(e, '.dx-datagrid-columns-separator');
        if (separator && separator.title !== hint) separator.title = hint;
    };
    const followUps = ['mousedown', 'pointerup', 'mouseup', 'dblclick'];
    element.addEventListener('pointerdown', onPointerDown, true);
    followUps.forEach((type) => element.addEventListener(type, onFollowUp, true));
    element.addEventListener('click', onClick, true);
    element.addEventListener('pointerover', onOver, true);
    element.addEventListener('pointermove', onPointerMove, true);
    return () => {
        endPressCursor();
        element.removeEventListener('pointermove', onPointerMove, true);
        element.removeEventListener('pointerdown', onPointerDown, true);
        followUps.forEach((type) => element.removeEventListener(type, onFollowUp, true));
        element.removeEventListener('click', onClick, true);
        element.removeEventListener('pointerover', onOver, true);
    };
};

export default attachHeaderBoundaryDblClick;
